import React, { useMemo, useState } from 'react';
import { Button, Skeleton, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@lognai/synapse-design-system-v2';
import { useInsights } from '../../hooks/use-insights';
import { InsightRow } from './InsightRow';
import { getRegisteredPluginIds } from '../../utils/entity-route-for';
import type { InsightSeverity, InsightStatus } from '../../api/insights';

const humanizePluginId = (id: string): string =>
  id.split(/[-_]/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

export interface InsightsListProps {
  status: InsightStatus;
  onNavigate?: (url: string) => void;
}

export const InsightsList: React.FC<InsightsListProps> = ({ status, onNavigate }) => {
  const [severity, setSeverity] = useState<InsightSeverity | 'all'>('all');
  const [pluginId, setPluginId] = useState<string>('all');
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const query = {
    status,
    ...(severity !== 'all' ? { severity } : {}),
    ...(pluginId !== 'all' ? { pluginId } : {}),
    cursor,
    limit: 20,
  };

  const { data, isLoading } = useInsights(query);

  // Plugin dropdown options derive from whichever UI plugins actually
  // registered entity routes (populated at app startup). Adding a new
  // *-ui plugin surfaces in the filter automatically.
  const pluginOptions = useMemo(() => getRegisteredPluginIds(), []);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Severity</label>
          <Select value={severity} onValueChange={(v) => { setSeverity(v as InsightSeverity | 'all'); setCursor(undefined); }}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="info">Info</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Plugin</label>
          <Select value={pluginId} onValueChange={(v) => { setPluginId(v); setCursor(undefined); }}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              {pluginOptions.map((id) => (
                <SelectItem key={id} value={id}>{humanizePluginId(id)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : !data || data.items.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <div className="text-base mb-1">No {status} insights</div>
          <div className="text-sm">The engine surfaces signals like late shipments and overdue POs here.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((insight) => (
            <InsightRow key={insight.id} insight={insight} onNavigate={onNavigate} />
          ))}
          {data.nextCursor && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setCursor(data.nextCursor ?? undefined)}>
                Next page →
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
